import * as React from 'react';
import { useLocalization } from '@fluent/react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import PublicIcon from '@mui/icons-material/Public';
import { countriesAtlas } from '../countriesApi';

export type CountryOption = {
    code: string;   // ISO-3166 alpha-2, np. "PL"
    name: string;   // np. "Poland"
    phone?: string; // np. "48"
};

export interface CountrySelectProps {
    /** Nazwa kraju LUB kod ISO (np. "Poland" albo "PL") */
    value?: string | null;
    /** Zwraca nazwę kraju oraz dopasowaną opcję (jeśli wybrano z listy) */
    onChange: (name: string | null, option: CountryOption | null) => void;

    /** What to display in the input field: 'name' (default) or 'code' */
    displayMode?: 'name' | 'code';

    /** Whether to show phone number in the dropdown options (default: false) */
    showPhoneNumber?: boolean;

    // (opcjonalne, ale bez przekazywania atlasu)
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    sx?: any;
}

const toOptions = () =>
    countriesAtlas
        .map(c => ({ code: c.code, name: c.label, phone: c.phone }))
        .sort((a, b) => a.name.localeCompare(b.name));

export const CountrySelect: React.FC<CountrySelectProps> = ({
    value,
    onChange,
    displayMode = 'name',
    showPhoneNumber = false,
    label = 'Country',
    placeholder = '',
    size = 'medium',
    fullWidth = true,
    disabled,
    required,
    error,
    helperText,
    sx,
}) => {
    const { l10n } = useLocalization();
    const norm = (s: string) => (s || '').trim().toLowerCase();
    const options = React.useMemo(toOptions, []);
    const resolvedPlaceholder = placeholder || String(l10n.getString('companies-markets-countries-placeholder', null, '_Start typing a country...'));
    // Pozwól na dopasowanie po nazwie lub kodzie
    const selected = React.useMemo(
        () => options.find(o => norm(o.name) === norm(value || '') || norm(o.code) === norm(value || '')) || null,
        [options, value]
    );
    const [inputValue, setInputValue] = React.useState<string>('');

    React.useEffect(() => {
        if (!value) {
            setInputValue('');
            return;
        }

        if (selected) {
            setInputValue(displayMode === 'code' ? selected.code : selected.name);
            return;
        }

        setInputValue(value);
    }, [value, selected, displayMode]);

    return (
        <Autocomplete<CountryOption, false, false, true>
            options={options}
            value={selected}
            inputValue={inputValue}
            onInputChange={(_, v) => {
                setInputValue(v);
                onChange(v || null, null); // synchronizacja podczas pisania
            }}
            onChange={(_, opt) => {
                const name = typeof opt === 'string' ? opt : opt?.name ?? '';
                onChange(name || null, (typeof opt === 'string' ? null : opt));
            }}
            autoHighlight
            
            getOptionLabel={(opt) => {
                if (typeof opt === 'string') return opt;
                return displayMode === 'code' ? opt.code : opt.name;
            }}
            isOptionEqualToValue={(a, b) => a.code === (b as CountryOption).code}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props as any;
                return (
                    <Box
                        key={key}
                        component="li"
                        sx={{ '& > img': { mr: 1.5, flexShrink: 0 } }}
                        {...optionProps}
                    >
                        {option.code ? (
                            <img
                                loading="lazy"
                                width="20"
                                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                alt=""
                            />
                        ) : null}
                        {option.name}
                        {option.code ? ` (${option.code})` : ''}
                        {showPhoneNumber && option.phone ? ` +${option.phone}` : ''}
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={resolvedPlaceholder}
                    size={size}
                    fullWidth={fullWidth}
                    disabled={disabled}
                    required={required}
                    error={error}
                    helperText={helperText}

                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <PublicIcon fontSize="small" sx={{ color: 'info.main' }} />
                            </InputAdornment>
                        ),
                    }}
                />
            )}
            sx={sx}
        />
    );
};

export default CountrySelect;
